import Button from '../../ui/Button';
import Heading from '../../ui/Heading';
import Input from '../../ui/Input';
import DragAndDropIcon from '../../assets/icons/DragAndDropIcon';
import DeleteIcon from '../../assets/icons/Delete';
import InputForDragAndDrop from '../../ui/InputForDragAndDrop';
import { useStepTwoInputs } from './bountiesHooks/useStepTwoInputs';
import TextWithTooltip from '../../ui/TextWithTooltip';
import CharacterLimit from '../../ui/CharacterLimit';
import { useWatch } from 'react-hook-form';
import FloatingButtons from '../../ui/FloatingButtons';
import { useSortable } from '@dnd-kit/sortable';
import DraggableList from '../../ui/DraggableComponent';
import { CSS } from '@dnd-kit/utilities';
import ButtonNew from '../../ui/ButtonNew';
import Loader from '../../ui/Loader';

const SortableCriteria = ({ microtaskField, index, remove, errors, control, criteriaNameLength }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: microtaskField.id, // Ensures each microtask has a unique identifier for drag-and-drop
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} key={microtaskField.id} className="flex  gap-3 mb-3 w-full">
            <div className=" cursor-grab active:cursor-grabbing" {...listeners} {...attributes}>
                <DragAndDropIcon className="relative top-3 " />
            </div>

            <div className="flex-1">
                <InputForDragAndDrop
                    name={`criteria.${index}.criteriaName`}
                    containerClass="flex-1"
                    control={control}
                    error={errors?.criteria ? errors?.criteria[index]?.criteriaName?.message : ''}
                    index={index}
                    className="w-full "
                    validateObject={{
                        required: 'Please enter a criteria',
                        maxLength: {
                            value: 200,
                            message: 'The maximum length is 200 characters',
                        },
                    }}
                />
                <CharacterLimit min={criteriaNameLength} max={200} />
            </div>
            <DeleteIcon className="relative top-3 cursor-pointer" onClick={() => remove(index)} />
        </div>
    );
};

function StepTwo({ setStep, register, control, errors, onSaveProgress, saving }) {
    const { maxAcceptancesPerUserField, criteriaFields, append, remove, swap, move } = useStepTwoInputs(control);
    const watchedCriteria = useWatch({ name: 'criteria', control });

    return (
        <div className="relative">
            <div className="p-12 pb-0  flex flex-col justify-between min-h-full   rounded-md  ">
                <div className="flex-grow ">
                    <div className="">
                        <div className="flex gap-3">
                            <Heading type="xsmall" className="capitalize  text-textPrimary">
                                Terms and Conditions
                            </Heading>
                        </div>
                    </div>
                    <div className="my-6">
                        <div className="flex gap-3">
                            <TextWithTooltip
                                heading={
                                    <Heading type="xsmall" className="capitalize  text-textPrimary">
                                        Maximum Attempts Per Bounty Hunter:
                                        <sup className="text-lg relative top-[1px]">*</sup>
                                    </Heading>
                                }
                            >
                                <p className="text-sm">
                                    This determines how many times the same user can attempt the bounty on Impact Miner.
                                </p>
                                <p className="text-sm mt-3">
                                    It depends on what type of bounty it is. For ex, if a bounty requires the user to
                                    submit an essay it could have less number of attempts but a bounty like taking
                                    surveys where the user has to collect information multiple times could have more
                                    attempts
                                </p>
                            </TextWithTooltip>
                        </div>

                        <Input
                            {...maxAcceptancesPerUserField}
                            type="number"
                            step="0"
                            error={errors?.maxAcceptancesPerUser?.message}
                            className="my-2 w-[300px]"
                            placeholder="Enter number of attempts"
                        />
                    </div>
                    <div className="mt-6">
                        <div className="flex gap-3">
                            <Heading type="xsmall" className="capitalize  text-textPrimary">
                                List Of Criteria: <sup className="text-lg relative top-[1px]">*</sup>
                            </Heading>
                        </div>
                        <p className="text-sm text-textSecondary">
                            Mention clearly the terms & conditions, instructions and criteria on who can participate,
                            any non-negotiable evaluation criteria for rewarding winners, disclaimers and any
                            information relevant. This information is displayed to the users on the Impact Miner
                        </p>
                    </div>
                    <div className="p-5 h-72 overflow-auto main">
                        <div className="  items-center">
                            <DraggableList
                                microtaskFields={criteriaFields}
                                move={move}
                                renderMicrotask={(criteriaField, index) => {
                                    const criteriaNameLength = watchedCriteria?.[index]?.criteriaName?.length || 0;
                                    return (
                                        <SortableCriteria
                                            microtaskField={criteriaField}
                                            index={index}
                                            remove={remove}
                                            criteriaNameLength={criteriaNameLength}
                                            errors={errors}
                                            control={control}
                                        />
                                    );
                                }}
                            />
                            <Button
                                type="button"
                                onClick={() => {
                                    append({
                                        criteriaName: '',
                                    });
                                }}
                                size="none"
                                className=" ml-5 font-semibold text-sm"
                                variant="transparent"
                            >
                                + Add Criteria
                            </Button>
                        </div>
                        <p className="text-foundryRed">{errors.criteria?.root?.message}</p>
                    </div>
                </div>
            </div>
            <FloatingButtons>
                <div className="flex items-center gap-3">
                    <ButtonNew
                        type="button"
                        fixedMinWidth={false}
                        variant="neutral"
                        className='min-w-16 text-neutral600'
                        size="sm"
                        onClick={() => setStep(1)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                            <path d="M1.25586 9.4986L8.14899 2.60547L9.26857 3.72505L4.2867 8.70693L17.4171 8.70693L17.4171 10.2903L4.2867 10.2903L9.26857 15.2721L8.14899 16.3917L1.25586 9.4986Z" fill="currentColor" />
                        </svg>
                    </ButtonNew>

                    <ButtonNew
                        type="button"
                        fixedMinWidth={true}
                        variant="neutral"
                        size="sm"
                        onClick={onSaveProgress}
                        disabled={saving}
                    >
                        {saving ? <Loader /> : 'Save as Draft'}
                    </ButtonNew>
                </div>

                <ButtonNew
                    type="button"
                    fixedMinWidth={true}
                    variant="blue"
                    size="sm"
                    onClick={() => setStep(3)}
                >
                    Next
                </ButtonNew>
            </FloatingButtons>
        </div>
    );
}

export default StepTwo;
